import { useState, useCallback } from 'react';
import { saveOrShare } from '../platform';

/**
 * useExportData - Reusable hook for exporting table data
 *
 * Provides three export methods:
 * 1. CSV download - Downloads data as a .csv file
 * 2. Copy as plain text - Copies data as tab-separated text (for pasting into Claude, spreadsheets, etc.)
 * 3. Copy as JSON - Copies data as formatted JSON
 *
 * @param {Object} options
 * @param {string} options.filename - Base filename for downloads (without extension)
 * @returns {Object} Export methods and state
 */
const useExportData = ({ filename = 'export' } = {}) => {
  const [copyStatus, setCopyStatus] = useState(null); // 'text' | 'json' | null

  /**
   * Generate CSV string from data
   */
  const generateCSV = useCallback((data, columns) => {
    if (!data || data.length === 0) return '';

    // Get headers from columns or first data row keys
    const headers = columns
      ? columns.map(col => col.label || col.key)
      : Object.keys(data[0]);

    const keys = columns
      ? columns.map(col => col.key)
      : Object.keys(data[0]);

    const rows = data.map(row =>
      keys.map(key => {
        let val = row[key];
        // Handle nested objects or arrays
        if (typeof val === 'object' && val !== null) {
          val = JSON.stringify(val);
        }
        const str = String(val ?? '');
        // Escape quotes and wrap in quotes if contains comma, newline, or quotes
        return str.includes(',') || str.includes('"') || str.includes('\n')
          ? `"${str.replace(/"/g, '""')}"`
          : str;
      })
    );

    return [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
  }, []);

  /**
   * Generate plain text (tab-separated) from data
   * Great for pasting into Claude, spreadsheets, etc.
   */
  const generatePlainText = useCallback((data, columns) => {
    if (!data || data.length === 0) return '';

    const headers = columns
      ? columns.map(col => col.label || col.key)
      : Object.keys(data[0]);

    const keys = columns
      ? columns.map(col => col.key)
      : Object.keys(data[0]);

    const rows = data.map(row =>
      keys.map(key => {
        let val = row[key];
        if (typeof val === 'object' && val !== null) {
          val = JSON.stringify(val);
        }
        return String(val ?? '').replace(/\t/g, ' ').replace(/\n/g, ' ');
      }).join('\t')
    );

    return [headers.join('\t'), ...rows].join('\n');
  }, []);

  /**
   * Download as CSV file
   */
  const downloadCSV = useCallback((data, columns) => {
    const csv = generateCSV(data, columns);
    if (!csv) return;

    // EXP-12: UTF-8 BOM so Excel-on-Windows doesn't mojibake em-dashes/accents.
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
    // Web: anchor download. Native iOS: share sheet (WKWebView has no
    // download UX — a plain anchor silently does nothing there).
    saveOrShare(blob, `${filename}-${new Date().toISOString().split('T')[0]}.csv`).catch((err) => {
      console.error('CSV export failed:', err);
    });
  }, [filename, generateCSV]);

  /**
   * Copy as plain text to clipboard
   */
  const copyAsText = useCallback(async (data, columns) => {
    const text = generatePlainText(data, columns);
    if (!text) return;

    try {
      await navigator.clipboard.writeText(text);
      setCopyStatus('text');
      setTimeout(() => setCopyStatus(null), 2000);
    } catch (err) {
      console.error('Failed to copy text:', err);
      // Fallback for older browsers
      const textarea = document.createElement('textarea');
      textarea.value = text;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopyStatus('text');
      setTimeout(() => setCopyStatus(null), 2000);
    }
  }, [generatePlainText]);

  /**
   * Copy as JSON to clipboard
   */
  const copyAsJSON = useCallback(async (data, columns) => {
    if (!data || data.length === 0) return;

    // If columns specified, only include those fields
    let jsonData = data;
    if (columns) {
      const keys = columns.map(col => col.key);
      jsonData = data.map(row => {
        const filtered = {};
        keys.forEach(key => {
          filtered[key] = row[key];
        });
        return filtered;
      });
    }

    const json = JSON.stringify(jsonData, null, 2);

    try {
      await navigator.clipboard.writeText(json);
      setCopyStatus('json');
      setTimeout(() => setCopyStatus(null), 2000);
    } catch (err) {
      console.error('Failed to copy JSON:', err);
      // Fallback
      const textarea = document.createElement('textarea');
      textarea.value = json;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopyStatus('json');
      setTimeout(() => setCopyStatus(null), 2000);
    }
  }, []);

  return {
    downloadCSV,
    copyAsText,
    copyAsJSON,
    copyStatus,
    // Utility to check status
    isCopied: (type) => copyStatus === type
  };
};

export default useExportData;
